import React, { useContext, useEffect, useState, type AriaAttributes } from 'react';

import { useSmartlingStrings } from '@lucid/hooks';

import Text from '../text/text';
import Box from '../box/box';
import Icon from '../icon/icon';
import Link from '../link/link';
import LayoutContext from '../context/layout-context';
import type { PaginationRenderProps } from './pagination';
import renderComponent, { type RenderComponent } from '../../utils/render-component';

type CallbackHandler = {
  /** The current activePage number. */
  activePage: number;
  /** The totalPages that have been paginated. */
  pageCount: number;
};

export type SimplePaginationProps<T> = {
  /** The children that are returned with the `Pagination` component. */
  children?: RenderComponent<PaginationRenderProps<T>>;
  /** Allows the component to get additional styles. */
  className?: string;
  /** Callback to set current ActivePage & current TotalPages. */
  handleCallback?: (values: CallbackHandler) => void;
  /** The list of items to return with pagination. */
  items?: T[];
  /** The number of items to be returned per each pagination page. `@default = 5` */
  itemsPerPage?: number;
  /** The onClick handler callback. */
  onClick?: (value: number) => void;
  /** Turns off the preventDefault behavior on links */
  shouldLink?: boolean;
  /** Returns all items not paginated. */
  shouldNotPaginate?: boolean;
  /** Changes the page to start from for the pagination data. */
  startPage?: number;
  /** The total number of available pages (if not to be infered from the number of items) */
  totalPages?: number;
  /** The root url alias for SEO crawling. */
  urlAlias?: string;
  /** The prefix appended to page numbers in the url */
  pagePrefix?: string;
} & AriaAttributes;

const SimplePagination = <T,>({
  children,
  className,
  handleCallback,
  startPage = 1,
  items = [],
  itemsPerPage = 5,
  onClick,
  shouldNotPaginate = false,
  shouldLink = false,
  totalPages,
  urlAlias,
  pagePrefix = '',
  ...rest
}: SimplePaginationProps<T>) => {
  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_blog_aria_label_pagination_next',
      'mktg_blog_aria_label_pagination_previous',
      'mktg_search_no_results',
    ],
    shortCode,
  });
  const [activePage, setActivePage] = useState(startPage);
  const pageCount = totalPages ? totalPages : shouldNotPaginate ? 1 : Math.ceil(items.length / itemsPerPage);
  const [willAllowNext, setWillAllowNext] = useState(activePage < pageCount);
  const [willAllowPrev, setWillAllowPrev] = useState(activePage > 1);

  const visibleItemStart = (activePage - 1) * itemsPerPage;
  const visibleItemEnd = activePage * itemsPerPage;
  const visibleItems = totalPages ? items : shouldNotPaginate ? items : items.slice(visibleItemStart, visibleItemEnd);
  const hasItems = !!visibleItems.length;

  useEffect(() => {
    if (handleCallback && startPage !== activePage) {
      handleCallback({
        activePage,
        pageCount,
      });
    }
  }, [activePage, handleCallback, pageCount, startPage]);

  useEffect(() => {
    // if any filtering of the items happens, we should return to the initial page
    setActivePage(startPage);
  }, [items, startPage]);

  useEffect(() => {
    setWillAllowNext(activePage < pageCount);
    setWillAllowPrev(activePage > 1);
  }, [activePage, pageCount]);

  const handleOnClick = (value: number) => {
    setActivePage(value);
    setWillAllowNext(value < pageCount);
    setWillAllowPrev(value > 1);

    if (onClick) {
      onClick(value);
    }
  };

  return (
    <>
      {children && (
        <Box
          css={{
            label: 'simplePaginationChildren',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {hasItems &&
            renderComponent({
              children,
              activePage,
              itemsPerPage,
              pageCount,
              visibleItems: visibleItems,
            })}
          {!hasItems && (
            <Text>{withSmartling('mktg_search_no_results') || "We couldn't find any results for your search."}</Text>
          )}
        </Box>
      )}
      <Box
        {...rest}
        as="nav"
        css={theme => ({
          label: 'simplePagination',
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          paddingTop: theme.space.s64,
          paddingBottom: theme.space.s40,
        })}
        // eslint-disable-next-line @stylistic/jsx-sort-props
        className={className}
        data-testid="paginationNavigationContainer"
      >
        {!shouldNotPaginate && pageCount > 1 && (
          <Box
            key="paginationUL"
            css={theme => ({
              margin: 0,
              label: 'simplePaginationNavigation',
              display: 'grid',
              gridTemplateColumns: `${theme.space.s24}px 1fr ${theme.space.s24}px`,
              alignItems: 'center',
              listStyle: 'none',
            })}
          >
            {willAllowPrev && (
              <Link
                aria-label={`${withSmartling('mktg_blog_aria_label_pagination_previous', { page: `${activePage - 1}` }) || 'Go to previous page'}${rest['aria-label'] ? `, ${rest['aria-label']}` : ''}`}
                css={theme => ({
                  color: theme.colors.coolGrey[50],
                  verticalAlign: 'middle',
                  textAlign: 'center',
                  outline: 'none',
                  '&:hover, &:focus': {
                    color: theme.colors.indigo[60],
                  },
                })}
                data-testid="paginationPreviousPage"
                href={
                  urlAlias
                    ? `${urlAlias.replace(/\/+$/, '')}/${activePage > 2 ? `${pagePrefix}${activePage - 1}` : ''}`
                    : undefined
                }
                onClick={e => {
                  if (!shouldLink) {
                    e.preventDefault();
                    handleOnClick(activePage - 1);
                  }
                }}
                rel="prev"
                role="button"
                tabIndex={0}
              >
                <Icon
                  css={{
                    fontSize: '1rem',
                    transform: 'rotate(180deg)',
                  }}
                  type="CarotRight"
                />
              </Link>
            )}
            <Text
              css={{ gridColumnStart: '2', margin: 'auto', textAlign: 'center', fontSize: 'inherit' }}
              variant="interactiveS500"
            >
              {`${activePage} of ${pageCount}`}
            </Text>
            {willAllowNext && (
              <Link
                aria-label={`${withSmartling('mktg_blog_aria_label_pagination_next', { page: `${activePage + 1}` }) || 'Go to next page'}${rest['aria-label'] ? `, ${rest['aria-label']}` : ''}`}
                css={theme => ({
                  gridColumnStart: '3',
                  color: theme.colors.coolGrey[50],
                  verticalAlign: 'middle',
                  outline: 'none',
                  textAlign: 'center',
                  '&:hover, &:focus': {
                    color: theme.colors.indigo[60],
                  },
                })}
                data-testid="paginationNextPage"
                href={urlAlias ? `${urlAlias.replace(/\/+$/, '')}/${pagePrefix}${activePage + 1}` : undefined}
                onClick={e => {
                  if (!shouldLink) {
                    e.preventDefault();

                    if (willAllowNext) {
                      handleOnClick(activePage + 1);
                    }
                  }
                }}
                rel="next"
                role="button"
                tabIndex={0}
              >
                <Icon
                  css={theme => ({
                    fontSize: theme.fontSizes.fs16,
                  })}
                  type="CarotRight"
                />
              </Link>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

export { SimplePagination };
export default SimplePagination;
